import { useTranslation } from 'react-i18next'
import {
  WorkflowRun,
  WorkflowRunStep,
  WorkflowTemplate,
} from '@digital-twins/shared-types'

import { LobbyEmptyState } from '@/components/shared/LobbyEmptyState'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import {
  WorkflowGateSubmission,
  WorkflowRunError,
} from '@/lib/workflows/useWorkflowRun'

import { collectGateArtifacts, isGateEditable } from './gateArtifacts'
import { RunGateCard } from './RunGateCard'
import { RunStepRow, runPaneRunStatusLabel } from './RunStepRow'
import { buildRunSpendLine } from './runSpendLine'
import { useRunSegmentQuote } from './useRunSegmentQuote'
import { useWidePane } from './useWidePane'
import { stimulusRefsFromRun } from '../stimulusRefsFromRun'

/**
 * What a person WATCHES while a workflow runs.
 *
 * Watching only: the decision to spend on twins is taken next to the work
 * (`ThreadRunGate` in the thread for prose, on the composer for questions),
 * and every gate rendered here is a read-only record of what is waiting or
 * what was decided.
 *
 * It renders inside `ThreadSourcesDrawer`, which is generic chrome: this
 * component owns the content, the drawer owns the shell, the width, the
 * resize and the open animation. Nothing about the drawer is duplicated here.
 *
 * The pane never reads a fixture. Everything arrives as props from
 * `useWorkflowRun()`, which is the seam Step 7.1 swaps for the live socket.
 */

interface ThreadRunPaneProps {
  run: WorkflowRun | null
  template: WorkflowTemplate | null
  isLoading: boolean
  /**
   * Why there is nothing to show, when the reason is a refusal rather than an
   * absence. `useWorkflowRun` returns null for a thread that simply has no run;
   * an error means the run could not be READ (notably the payment guard, which
   * covers the reads too) or an action was declined. Optional so every call
   * site that predates the live transport still compiles.
   */
  error?: WorkflowRunError | null
  /**
   * When false, gate steps render as plain `RunStepRow`s with no `RunGateCard`.
   * The thread rail's Progress section uses this so approve/cancel stays only
   * on the composer (`ThreadRunGate`). Default true for standalone mounts.
   */
  includeGateCards?: boolean
  /** Absent for a viewer of a shared thread: no step offers a retry. */
  onRetryStep?: (stepId: string) => void
  onSubmitGate: (submission: WorkflowGateSubmission) => void
  onCancelRun: () => void
  /** Thread / answer language for seeded step titles. */
  language?: string | null
}

/** Fan-out width the template declared, so a partial frame knows what is missing. */
function expectedArtifactCount(
  step: WorkflowRunStep,
  template: WorkflowTemplate | null,
): number {
  const config = template?.steps.find((candidate) => candidate.key === step.stepKey)
  if (config && config.kind === 'prompt') {
    return config.count ?? 1
  }
  return 1
}

/** Matches the loaded layout row for row, so nothing jumps when the run lands. */
function RunPaneSkeleton() {
  return (
    <div className="space-y-3 p-3" data-testid="thread-run-pane-loading">
      <Skeleton className="h-4 w-40 rounded" />
      <div className="space-y-2.5">
        {Array.from({ length: 5 }).map((_unused, index) => (
          <div key={index} className="flex items-start gap-2.5">
            <Skeleton className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full" />
            <Skeleton className="h-4 flex-1 rounded" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function ThreadRunPane(props: ThreadRunPaneProps) {
  const {
    run,
    template,
    isLoading,
    error = null,
    includeGateCards = true,
    onRetryStep,
    onSubmitGate,
    onCancelRun,
    language = null,
  } = props

  const { t } = useTranslation('app')

  const isWide = useWidePane()
  /* Same quote as the deciding gate: min(panel, matching) for a filtered run. */
  const segmentQuote = useRunSegmentQuote(
    run?.status === 'awaiting_approval' ? run : null,
    template ?? null,
  )

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col" data-testid="thread-run-pane">
        <RunPaneSkeleton />
      </div>
    )
  }

  /* Ahead of the empty state below, and never merged into it: "no run in this
     thread" invites the reader to start one, which is the wrong thing to say to
     somebody whose run exists and was refused. The kind is on the element so a
     payment refusal can be told from a failed request without parsing copy.

     Guarded on there being no run to show, because the same field also reports a
     REFUSED ACTION (`useWorkflowRun` sets it when a gate submission, a retry or
     a cancel comes back 4xx) and the error is not cleared until the next
     successful one. Taking the pane over in that case would delete a live run
     and the gate card being worked in from under the reader; that case renders
     the notice below the header instead. */
  if (error && !run) {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col justify-center p-3"
        data-testid="thread-run-pane"
        data-run-error={error.kind}
      >
        <LobbyEmptyState
          title={t('threads.detail.runPane.unavailable')}
          description={error.message}
        />
      </div>
    )
  }

  if (!run) {
    return (
      <div
        className="flex min-h-0 flex-1 flex-col justify-center p-3"
        data-testid="thread-run-pane"
      >
        <LobbyEmptyState
          title={t('threads.detail.runPane.emptyTitle')}
          description={t('threads.detail.runPane.emptyDescription')}
        />
      </div>
    )
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col overflow-y-auto p-3"
      data-testid="thread-run-pane"
      data-run-status={run.status}
      {...(error ? { 'data-run-error': error.kind } : {})}
    >
      <div className="flex items-baseline justify-between gap-2 px-1 pb-1">
        <Text size="sm" weight="medium" className="min-w-0 truncate">
          {run.templateDisplayName}
        </Text>
        <Text size="xs" color="muted" className="shrink-0">
          {runPaneRunStatusLabel(run.status)}
        </Text>
      </div>

      {/* A refused action on a run that is still on screen: said once, above
          the steps, so the decision the reader just tried is not silently
          dropped and the run they are watching is not taken away. */}
      {error ? (
        <Text
          size="xs"
          className="px-1 pb-2 text-chart-warning"
          data-testid="thread-run-pane-error"
        >
          {error.message}
        </Text>
      ) : null}

      <ul className="divide-y divide-border/50">
        {run.steps.map((step) => {
          const isGate = step.stepKind === 'gate'
          const isDecidedGate =
            isGate && (step.gateDecision !== null || step.state === 'completed')
          const showsGate =
            includeGateCards &&
            isGate &&
            (step.state === 'awaiting_approval' || isDecidedGate)

          return (
            <RunStepRow
              key={step.stepId}
              step={step}
              expectedArtifactCount={expectedArtifactCount(step, template)}
              onRetry={onRetryStep ? () => onRetryStep(step.stepId) : undefined}
              templateSlug={run.templateSlug}
              language={language}
            >
              {showsGate ? (
                <RunGateCard
                  step={step}
                  artifacts={collectGateArtifacts(run, step, template)}
                  /* WATCH-ONLY. The decision lives in the thread
                     (`ThreadRunGate`), where the reader already is; the pane
                     shows what is waiting and what it would cost, and nothing
                     here can spend it.

                     No `editState` either, so this copy falls back to its own
                     and that map stays empty: a card that cannot edit has
                     nothing to stage, and staged bodies belong to the mount
                     that decides. */
                  actionable={false}
                  canEdit={isWide && isGateEditable(step, template)}
                  viewerOwnsRun={run.viewerOwnsRun}
                  spendLine={buildRunSpendLine(run, template, step, undefined, {
                    matchingCount: segmentQuote.matchingCount,
                  })}
                  stimulusRefs={stimulusRefsFromRun(run, template)}
                  language={language}
                  onSubmit={onSubmitGate}
                  onCancelRun={onCancelRun}
                />
              ) : null}
            </RunStepRow>
          )
        })}
      </ul>
    </div>
  )
}
